/**
 * A photograph to a fingerprint: the client's half of a scan.
 *
 * Four shared calls, no logic of the client's own:
 *
 *  1. `captureCard` finds the card in the capture and rectifies it onto the
 *     card's own coordinates. A capture with no card in it is a *finding* - the
 *     surface shows "no card was found" and the operator takes another one - not
 *     an exception.
 *  2. `classifyFrame` reads the frame's era off the picture's edges and the footer
 *     band, which is what tells the cascade which footer template a card that had
 *     an OCR engine would be read with.
 *  3. `artProbeHash` cuts the constant art window and fingerprints it - the exact
 *     rectangle the index was swept for (ADR 0008), which is why it is the core's
 *     function and not a few lines here.
 *  4. The fingerprint goes to the API, which owns the index and the catalog. A
 *     phone cannot hold a 117,890-printing catalog comfortably, and the cascade's
 *     fusion runs where the data is.
 */

import {
  artProbeHash,
  captureCard,
  classifyFrame,
  type FrameClass,
  type FrameImage,
  type GreyImage,
} from '@unsorted/shared';
import { decodeCapture, type DecodeOptions } from './decode.js';

/** The frame's width. 320 keeps a 626px art crop well sampled; the tool uses the same. */
export const FRAME_WIDTH = 320;

export interface CaptureRead {
  /** The card on its own coordinates, or null when the capture held no card. */
  frame: FrameImage | null;
  frameClass: FrameClass;
  frameConfidence: number;
  /** The art fingerprint the server matches against the index, or null. */
  artPhash: string | null;
  /** The card's share of the capture's width, as the detector's own confidence. */
  coverage: number;
  /** What the core found, in its own words. Shown when a capture is refused. */
  notes: string[];
}

/** Everything except the decode: pure, and the same on a phone as in a browser. */
export function readCapture(grey: GreyImage): CaptureRead {
  const captured = captureCard(grey, { width: FRAME_WIDTH });
  if (captured.frame === null) {
    return {
      frame: null,
      frameClass: 'unknown',
      frameConfidence: 0,
      artPhash: null,
      coverage: captured.coverage,
      notes: captured.notes,
    };
  }
  const classification = classifyFrame(captured.frame);
  return {
    frame: captured.frame,
    frameClass: classification.frameClass,
    frameConfidence: classification.confidence,
    artPhash: artProbeHash(captured.frame),
    coverage: captured.coverage,
    notes: [...captured.notes, ...classification.notes],
  };
}

/** Decode a captured file and read it. Injected in tests, where there is no canvas. */
export type CaptureSource = (file: Blob) => Promise<CaptureRead>;

export async function captureFile(file: Blob, options: DecodeOptions = {}): Promise<CaptureRead> {
  return readCapture(await decodeCapture(file, options));
}
